import type { PR } from "@/types/evolve";

// Common lift patterns
const LIFT_PATTERNS = [
  // Format: "Bench 225x5" or "Bench press 225 lbs x 5 reps"
  /\b(bench\s*press|bench|squat|deadlift|overhead\s*press|ohp|military\s*press|barbell\s*row|bent\s*over\s*row|pull[-\s]?ups?|chin[-\s]?ups?|dips?|leg\s*press|shoulder\s*press|incline\s*bench|decline\s*bench|front\s*squat|romanian\s*deadlift|rdl|hip\s*thrust|lunges?|calf\s*raise|lat\s*pulldown|cable\s*row|t-bar\s*row|pendlay\s*row|power\s*clean|clean\s*and\s*jerk|snatch)\s*[:\s-]?\s*(\d{2,4})\s*(?:lbs?|pounds?|kg)?\s*(?:x|×|for)?\s*(\d{1,3})?\s*(?:reps?|x)?/gi,
  
  // Format: "Hit a new PR on bench: 275 lbs"
  /(?:new\s*)?(?:pr|personal\s*record)\s*(?:on|for|in)?\s*(bench\s*press|bench|squat|deadlift|overhead\s*press|ohp|military\s*press|barbell\s*row)[:\s-]?\s*(\d{2,4})\s*(?:lbs?|pounds?|kg)?/gi,
  
  // Format: "I hit 315 on squat" or "Got 225 on bench"
  /(?:hit|got|lifted|pushed|pulled|did)\s*(\d{2,4})\s*(?:lbs?|pounds?|kg)?\s*(?:on|for)?\s*(bench\s*press|bench|squat|deadlift|overhead\s*press|ohp)/gi,
];

// Normalize lift names
const LIFT_ALIASES: Record<string, string> = {
  'bench press': 'Bench Press',
  'bench': 'Bench Press',
  'squat': 'Squat',
  'deadlift': 'Deadlift',
  'overhead press': 'Overhead Press',
  'ohp': 'Overhead Press',
  'military press': 'Overhead Press',
  'shoulder press': 'Overhead Press',
  'barbell row': 'Barbell Row',
  'bent over row': 'Barbell Row',
  'pendlay row': 'Barbell Row',
  't-bar row': 'T-Bar Row',
  'pull-ups': 'Pull-Ups',
  'pullups': 'Pull-Ups',
  'pull ups': 'Pull-Ups',
  'chin-ups': 'Chin-Ups',
  'chinups': 'Chin-Ups',
  'chin ups': 'Chin-Ups',
  'dips': 'Dips',
  'dip': 'Dips',
  'leg press': 'Leg Press',
  'incline bench': 'Incline Bench',
  'decline bench': 'Decline Bench',
  'front squat': 'Front Squat',
  'romanian deadlift': 'Romanian Deadlift',
  'rdl': 'Romanian Deadlift',
  'hip thrust': 'Hip Thrust',
  'lunges': 'Lunges',
  'lunge': 'Lunges',
  'calf raise': 'Calf Raise',
  'lat pulldown': 'Lat Pulldown',
  'cable row': 'Cable Row',
  'power clean': 'Power Clean',
  'clean and jerk': 'Clean & Jerk',
  'snatch': 'Snatch',
};

function normalizeLiftName(name: string): string {
  const lower = name.toLowerCase().trim();
  return LIFT_ALIASES[lower] || name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
}

export interface DetectedPR {
  lift: string;
  weight: number;
  reps?: number;
}

/**
 * Detect potential PRs mentioned in a message
 */
export function detectPRsInMessage(message: string): DetectedPR[] {
  const detected: DetectedPR[] = [];
  const seen = new Set<string>();
  
  // Try each pattern
  for (const pattern of LIFT_PATTERNS) {
    // Reset regex state
    pattern.lastIndex = 0;
    
    let match;
    while ((match = pattern.exec(message)) !== null) {
      let lift: string;
      let weight: number;
      let reps: number | undefined;
      
      // Different patterns have different capture group orders
      if (match[1] && isNaN(parseInt(match[1]))) {
        // Lift name is first
        lift = normalizeLiftName(match[1]);
        weight = parseInt(match[2]);
        reps = match[3] ? parseInt(match[3]) : undefined;
      } else if (match[2] && isNaN(parseInt(match[2]))) {
        // Weight is first, lift is second
        weight = parseInt(match[1]);
        lift = normalizeLiftName(match[2]);
        reps = match[3] ? parseInt(match[3]) : undefined;
      } else {
        continue;
      }
      
      // Validate weight is reasonable (10-1500 lbs)
      if (weight < 10 || weight > 1500) continue;
      
      // Avoid duplicates
      const key = `${lift}-${weight}`;
      if (seen.has(key)) continue;
      seen.add(key);
      
      detected.push({ lift, weight, reps });
    }
  }
  
  return detected;
}

/**
 * Check if a detected PR is actually a new record
 */
export function isNewPR(detected: DetectedPR, existingPRs: PR[]): boolean {
  const existingForLift = existingPRs.filter(
    pr => pr.lift.toLowerCase() === detected.lift.toLowerCase()
  );
  
  if (existingForLift.length === 0) return true;
  
  const maxExisting = Math.max(...existingForLift.map(pr => pr.weight));
  return detected.weight > maxExisting;
}

/**
 * Find PRs in a user message that should be synced
 */
export function findPRsToSync(
  userMessage: string, 
  existingPRs: PR[]
): DetectedPR[] {
  const detected = detectPRsInMessage(userMessage);
  return detected.filter(pr => isNewPR(pr, existingPRs));
}
